import {
  Component,
  effect,
  inject,
  OnInit,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormField, form, required } from '@angular/forms/signals';
import { FormsModule } from '@angular/forms';
import { EAtajoRangoTiempo } from '@core/enums/atajoRangoTiempo.enum';
import { ETipoMovimientoCaja } from '@core/enums/tipoMovimientoCaja.enum';
import { IEmpleadoData } from '@core/interfaces/entidades/empleado.entidad';
import { IOptionsSelect } from '@core/interfaces/options.select.interface';
import { IPaginar } from '@core/interfaces/paginacion/paginacion.interface';
import { ITablaColumna } from '@core/interfaces/tabla.interface';
import { EmpleadoApiService } from '@core/services/api/empleado-api.service';
import { TipoPagoApiService } from '@core/services/api/tipo-pago-api.service';
import { FormErrorMessagePipe } from '@shared/pipes/form-error-message.pipe';
import { AppService } from '@shared/services/app.service';
import { ToastService } from '@shared/services/toast.service';
import { calcularAtajoRangoFecha } from '@shared/utils/functions.utils';
import { UiDatepicker, UiDialog, UiSelect } from '@shared/components/ui';
import { FiltrosReporte } from '@shared/components/filtros-reporte/filtros-reporte';
import { MovimientoCajaApiService } from '@core/services/api/movimiento-caja.service';
import {
  IReporteMovimientoCajaFiltroRq,
  IReporteMovimientoCajaRes,
  IResumenReporteMovimientoCaja,
} from '@core/interfaces/request-response/movimiento-caja.dto.interface';
import { Tabla } from '@shared/components/tabla/tabla';
import { Paginacion } from '@shared/components/paginacion/paginacion';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { environment } from '@env/environment';
import { VerVenta } from '@shared/components/ver-venta/ver-venta';
import { SucursalService } from '@shared/services/sucursal.service';
import { ISucursalData } from '@core/interfaces/entidades/sucursal.entidad';
import { NombreSucursalPipe } from '../../../shared/pipes/nombre-sucursal.pipe';
import { descargarArchivoDesdeResponse } from '@shared/utils/functions.utils';

@Component({
  selector: 'app-reporte-movimiento-caja',
  imports: [
    FormsModule,
    FormField,
    UiDatepicker,
    FormErrorMessagePipe,
    UiSelect,
    FiltrosReporte,
    Tabla,
    Paginacion,
    CurrencyPipe,
    UiDialog,
    DatePipe,
    VerVenta,
    NombreSucursalPipe,
  ],
  templateUrl: './reporte-movimiento-caja.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './reporte-movimiento-caja.css',
})
export class ReporteMovimientoCaja implements OnInit {
  constructor() {
    effect(() => {
      if (this.sucursalService.sucursalReady() && this.appService.authReady()) {
        this.cargarOpcionesTipoMovimiento();
        this.cargarTiposPago();
        this.cargarEmpleados();
        this.cargarOpcionesSucursal();
      }
    });
  }

  ngOnInit(): void {
    this.appService.setPanelHeader({
      title: 'Reporte de Movimiento de Caja',
      subtitle: 'Visualiza el reporte de movimiento de caja',
      showBackButton: true,
      icon: 'bi bi-cash-stack',
    });
    this.columnasTabla = [
      { key: 'sucursal_id', titulo: 'Sucursal', template: this.sucursalColumnTemplate() },
      { key: 'fecha', titulo: 'Fecha', pipe: new DatePipe(environment.locale) },
      { key: 'codigo_caja', titulo: 'Código Caja' },
      { key: 'usuario_nombre_completo', titulo: 'Empleado' },
      { key: 'monto', titulo: 'Monto', pipe: new CurrencyPipe(environment.locale, 'PEN') },
      { key: 'tipo', titulo: 'Tipo de Movimiento' },
      { key: 'tipo_pago_nombre', titulo: 'Tipo de Pago' },
    ];
  }

  // servicios
  private toastService = inject(ToastService);
  private appService = inject(AppService);
  private tipoPagoApiService = inject(TipoPagoApiService);
  private empleadoApiService = inject(EmpleadoApiService);
  private movimientoCajaApiService = inject(MovimientoCajaApiService);
  private sucursalService = inject(SucursalService);
  canSelectSucursal = this.sucursalService.canSelectSucursal;
  // datos
  private itemsPorPagina = 18;
  private requestFiltro: IReporteMovimientoCajaFiltroRq | null = null;

  ETipoMovimientoCaja = ETipoMovimientoCaja;
  EAtajoRangoTiempo = EAtajoRangoTiempo;
  maxDate = new Date(new Date().setHours(23, 59, 59, 999));
  minDate = new Date(new Date().setFullYear(new Date().getFullYear() - 1));
  columnasTabla: ITablaColumna[] = [];
  paginacion = signal<IPaginar<IReporteMovimientoCajaRes> | null>(null);
  resumenReporte = signal<IResumenReporteMovimientoCaja | null>(null);
  movimiento_seleccionado = signal<IReporteMovimientoCajaRes | null>(null);
  modal_ver_movimientos_caja = signal(false);

  formFiltro = form(
    signal({
      rangoFecha: null as Date[] | null,
      empleado: null as IEmpleadoData | null,
      tipo_movimiento: null as ETipoMovimientoCaja | null,
      tipo_pago: null as number | null,
      sucursal: null as ISucursalData | null,
    }),
    (x) => {
      required(x.rangoFecha, { message: 'El rango de fechas es obligatorio' });
    },
  );
  // opciones del selector
  opcionesTipoMovimiento = signal<IOptionsSelect<ETipoMovimientoCaja | null>[]>([]);
  opcionesTipoPago = signal<IOptionsSelect<number | null>[]>([]);
  optionesEmpleado = signal<IOptionsSelect<IEmpleadoData | null>[]>([]);
  opcionesSucursal = signal<IOptionsSelect<ISucursalData | null>[]>([]);
  // metodos
  private cargarOpcionesTipoMovimiento() {
    this.opcionesTipoMovimiento.set(
      Object.values(ETipoMovimientoCaja).map((tipoMovimiento) => ({
        label: tipoMovimiento,
        value: tipoMovimiento,
      })),
    );
    this.opcionesTipoMovimiento.update((options) => [
      {
        label: 'Todos',
        value: null,
      },
      ...options,
    ]);
  }
  private cargarEmpleados() {
    this.empleadoApiService.cargar().subscribe({
      next: (empleados) => {
        this.optionesEmpleado.set(
          empleados.map((empleado) => ({
            label: empleado.nombre_completo,
            value: empleado,
          })),
        );
        // agregamos opción de todos
        this.optionesEmpleado.update((options) => [
          {
            label: 'Todos los empleados',
            value: null,
          },
          ...options,
        ]);
      },
      error: () => {
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          'No se pudieron cargar los empleados para el filtro',
        );
      },
    });
  }
  private cargarOpcionesSucursal() {
    const sucursales = this.sucursalService.sucursales();
    const restringidaId = this.sucursalService.sucursalRestringidaId();

    if (restringidaId != null) {
      const restringida = sucursales.find((s) => s.id === restringidaId);
      this.opcionesSucursal.set(
        restringida ? [{ label: restringida.nombre, value: restringida }] : [],
      );
      this.formFiltro.sucursal().value.set(restringida ?? null);
      return;
    }

    this.opcionesSucursal.set(
      sucursales.map((sucursal) => ({
        label: sucursal.nombre,
        value: sucursal,
      })),
    );
    this.opcionesSucursal.update((options) => [
      {
        label: 'Todas las sucursales',
        value: null,
      },
      ...options,
    ]);
  }
  private cargarTiposPago() {
    this.tipoPagoApiService.cargarTodos().subscribe((tiposPago) => {
      this.opcionesTipoPago.set(
        tiposPago.map((tipoPago) => ({
          label: tipoPago.nombre,
          value: tipoPago.id,
        })),
      );
      this.opcionesTipoPago.update((options) => [
        {
          label: 'Todos',
          value: null,
        },
        ...options,
      ]);
    });
  }
  cambiarPagina(pagina: number) {
    if (!this.requestFiltro) return;
    this.movimientoCajaApiService
      .paginarReporte(this.requestFiltro, pagina, this.itemsPorPagina)
      .subscribe({
        next: (rs) => {
          const { paginar, resumen } = rs;
          this.paginacion.set({
            ...paginar,
            pagina: pagina,
          });
          this.resumenReporte.set(resumen);
        },
        error: (err) => {
          if (err.status == 401) return;
          this.toastService.show(
            'top-right',
            'error',
            'Error',
            err.error.message ||
              'No se pudo cargar el reporte de movimiento de caja con los filtros aplicados',
          );
        },
      });
  }
  private checkInvalidForm(): boolean {
    if (this.formFiltro().invalid()) {
      this.markAllAsTouched();
      return true;
    }
    return false;
  }
  private markAllAsTouched() {
    this.formFiltro.rangoFecha().markAsTouched();
  }
  limpiarFiltros() {
    this.formFiltro.rangoFecha().value.set([]);
    this.formFiltro.empleado().value.set(null);
    this.formFiltro.tipo_movimiento().value.set(null);
    this.formFiltro.tipo_pago().value.set(null);
    if (this.canSelectSucursal()) {
      this.formFiltro.sucursal().value.set(null);
    }
  }
  aplicarFiltros(event: Event) {
    event.preventDefault();
    if (this.checkInvalidForm()) return;
    const rq = this.buildRequestFiltro();
    if (!rq) return;

    this.requestFiltro = rq;
    this.cambiarPagina(1);
  }

  exportarExcel() {
    if (this.checkInvalidForm()) return;
    const rq = this.buildRequestFiltro();
    if (!rq) return;

    this.movimientoCajaApiService.exportarReporteExcel(rq).subscribe({
      next: (response) => {
        descargarArchivoDesdeResponse(response, 'reporte-movimiento-caja.xlsx');
        this.toastService.show(
          'top-right',
          'success',
          'Excel',
          'Reporte de movimiento de caja descargado.',
        );
      },
      error: (err) => {
        if (err.status == 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error?.message || 'No se pudo exportar el reporte de movimiento de caja.',
        );
      },
    });
  }

  private buildRequestFiltro(): IReporteMovimientoCajaFiltroRq | null {
    const { rangoFecha, empleado, tipo_movimiento, tipo_pago, sucursal } =
      this.formFiltro().controlValue();
    if (!rangoFecha || rangoFecha.length !== 2) return null;

    return {
      fecha_inicio: rangoFecha[0],
      fecha_fin: rangoFecha[1],
      sucursal_id: sucursal ? sucursal.id : undefined,
      empleado_id: empleado ? empleado.id : undefined,
      tipo_movimiento: tipo_movimiento ? tipo_movimiento : undefined,
      tipo_pago_id: tipo_pago ? Number(tipo_pago) : undefined,
    };
  }
  atajoRangoFecha(tiempo: EAtajoRangoTiempo) {
    const { inicio, fin } = calcularAtajoRangoFecha(tiempo);
    this.formFiltro.rangoFecha().value.set([inicio, fin]);
  }
  verDetallesMovimientoCaja(movimientoCaja: IReporteMovimientoCajaRes) {
    this.movimiento_seleccionado.set(movimientoCaja);
    this.modal_ver_movimientos_caja.set(true);
  }

  private ver_detalles_venta_component = viewChild<VerVenta>('verVentaComponent');
  mostrando_detalles_venta = signal(false);
  verDetallesVenta() {
    const movimiento = this.movimiento_seleccionado();
    if (!movimiento || !movimiento.venta_id || this.mostrando_detalles_venta() == true) return;
    this.ver_detalles_venta_component()?.mostrarDetalles(movimiento.venta_id);
    this.mostrando_detalles_venta.set(true);
  }
  onCloseModalVerMovimientoCaja() {
    this.movimiento_seleccionado.set(null);
    this.modal_ver_movimientos_caja.set(false);
    this.mostrando_detalles_venta.set(false);
  }

  private sucursalColumnTemplate = viewChild('sucursalColumnTemplate');
}
